import { Fragment, useCallback, useEffect, useState } from "react";
import { api, ApiError } from "../lib/api";
import type { CardTransferBalance, PartnerPotential, Person, SolveResult, TransferPartner } from "../lib/types";
import { formatINR, formatPoints } from "../lib/format";
import { Badge, EmptyState, ErrorBanner, PageHeader, Panel, Spinner, StatTile } from "../components/ui";
import { PartnerPotentialChart } from "../components/charts";
import { TransferSetup } from "../components/TransferSetup";
import { TransferLog } from "../components/TransferLog";

type Tab = "potential" | "plan" | "log" | "setup";

const TABS: { key: Tab; label: string }[] = [
  { key: "potential", label: "What I can reach" },
  { key: "plan", label: "Plan a redemption" },
  { key: "log", label: "Transfers" },
  { key: "setup", label: "Partners & routes" },
];

export default function TransfersPage() {
  const [tab, setTab] = useState<Tab>("potential");
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  return (
    <>
      <PageHeader
        title="Miles & transfer partners"
        subtitle="What your points are worth once they leave the card — and the cheapest way to hit a redemption target."
      />

      <div className="mb-6 flex flex-wrap gap-1 border-b border-ink-200">
        {TABS.map((t) => (
          <button
            key={t.key}
            onClick={() => { setTab(t.key); setError(null); setNotice(null); }}
            className={`-mb-px border-b-2 px-4 py-2 text-sm font-medium transition-colors ${
              tab === t.key ? "border-accent-600 text-accent-800" : "border-transparent text-ink-500 hover:text-ink-800"
            }`}
            aria-current={tab === t.key ? "page" : undefined}
          >
            {t.label}
          </button>
        ))}
      </div>

      {error && <ErrorBanner message={error} onDismiss={() => setError(null)} />}
      {notice && (
        <div className="mb-4 rounded-lg border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm text-emerald-800">
          {notice}
        </div>
      )}

      {tab === "potential" && <PotentialTab onError={setError} />}
      {tab === "plan" && <PlanTab onError={setError} />}
      {tab === "log" && <TransferLog onError={setError} onNotice={setNotice} />}
      {tab === "setup" && <TransferSetup onError={setError} onNotice={setNotice} />}
    </>
  );
}

/* ------------------------------------------------------------- potential */

function PotentialTab({ onError }: { onError: (m: string) => void }) {
  const [data, setData] = useState<{ potential: PartnerPotential[]; balances: CardTransferBalance[] } | null>(null);
  const [expanded, setExpanded] = useState<string | null>(null);
  const [showEmpty, setShowEmpty] = useState(false);

  useEffect(() => {
    api
      .get<{ potential: PartnerPotential[]; balances: CardTransferBalance[] }>("/transfers/potential")
      .then(setData)
      .catch((err) => onError(err instanceof ApiError ? err.message : "Could not work out your potential"));
  }, [onError]);

  if (!data) return <Spinner />;

  const reachable = data.potential.filter((p) => p.potentialUnits > 0);
  const shown = showEmpty ? data.potential : reachable;
  const totalValue = data.potential.reduce((s, p) => s + p.potentialValue, 0);
  const transferablePoints = data.balances.reduce((s, b) => s + b.availablePoints, 0);
  const bestRate = reachable
    .filter((p) => p.reachableUnits > 0)
    .sort((a, b) => b.valuePerUnit - a.valuePerUnit)[0];

  if (data.potential.length === 0) {
    return <EmptyState title="No transfer partners yet" hint="Add partners and routes under “Partners & routes”." />;
  }

  return (
    <>
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <StatTile
          label="Points ready to transfer"
          value={formatPoints(transferablePoints)}
          sub={`Across ${data.balances.length} card${data.balances.length === 1 ? "" : "s"}`}
        />
        <StatTile
          label="Programmes you can reach"
          value={String(reachable.filter((p) => p.reachableUnits > 0).length)}
          sub={`of ${data.potential.length} set up`}
        />
        <StatTile
          label="Potential value"
          value={formatINR(totalValue)}
          sub="If every route were maxed out — you can only spend each point once"
        />
        <StatTile
          label="Best value per mile"
          value={bestRate ? `₹${bestRate.valuePerUnit}` : "—"}
          sub={bestRate ? bestRate.partnerName : "Set a value on each partner"}
          tone="good"
        />
      </div>

      <div className="mt-6">
        <Panel
          title="Potential value by programme"
          subtitle="Miles valued in rupees, because a Hilton point and an Accor point are not the same thing"
        >
          {reachable.length > 0 ? (
            <PartnerPotentialChart data={reachable.slice().sort((a, b) => b.potentialValue - a.potentialValue)} />
          ) : (
            <EmptyState title="Nothing reachable yet" hint="Add routes from your reward programmes to a partner." />
          )}
        </Panel>
      </div>

      <div className="mt-6">
        <Panel
          title="Every programme"
          subtitle="What you hold, and what you could add today within your caps"
          actions={
            <label className="flex items-center gap-2 text-xs text-ink-600">
              <input
                type="checkbox"
                className="h-3.5 w-3.5 rounded border-ink-300 text-accent-600 focus:ring-accent-500"
                checked={showEmpty}
                onChange={(e) => setShowEmpty(e.target.checked)}
              />
              Show ones I can't reach
            </label>
          }
        >
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-ink-200">
                  <th className="th">Programme</th>
                  <th className="th text-right">Held</th>
                  <th className="th text-right">Can add</th>
                  <th className="th text-right">Potential</th>
                  <th className="th text-right">Worth</th>
                  <th className="th"><span className="sr-only">Detail</span></th>
                </tr>
              </thead>
              <tbody className="divide-y divide-ink-100">
                {shown.map((p) => (
                  <Fragment key={p.partnerId}>
                    <tr className={expanded === p.partnerId ? "bg-ink-50/60" : undefined}>
                      <td className="td">
                        <span className="font-medium text-ink-900">{p.partnerName}</span>
                        <span className="block text-xs text-ink-500">
                          {p.kind === "HOTEL" ? "Hotel" : p.kind === "AIRLINE" ? "Airline" : "Other"}
                          {p.alliance ? ` · ${p.alliance}` : ""} · ₹{p.valuePerUnit}/mile
                        </span>
                      </td>
                      <td className="td tnum text-right text-ink-600">{formatPoints(p.balanceUnits)}</td>
                      <td className="td tnum text-right">
                        {p.reachableUnits > 0 ? (
                          <span className="text-emerald-700">+{formatPoints(p.reachableUnits)}</span>
                        ) : (
                          <span className="text-ink-300">—</span>
                        )}
                      </td>
                      <td className="td tnum text-right font-semibold">{formatPoints(p.potentialUnits)}</td>
                      <td className="td tnum text-right">{formatINR(p.potentialValue)}</td>
                      <td className="td text-right">
                        {p.sources.length > 0 && (
                          <button
                            className="text-xs text-accent-700 underline"
                            onClick={() => setExpanded(expanded === p.partnerId ? null : p.partnerId)}
                            aria-expanded={expanded === p.partnerId}
                          >
                            {expanded === p.partnerId ? "Hide" : "From where"}
                          </button>
                        )}
                      </td>
                    </tr>
                    {expanded === p.partnerId && (
                      <tr className="bg-ink-50/60">
                        <td className="td" colSpan={6}>
                          <ul className="space-y-2">
                            {p.sources.map((s, i) => (
                              <li key={`${s.sourceId}-${i}`} className="flex flex-wrap items-baseline gap-x-3 gap-y-1 text-xs">
                                <span className="inline-flex items-center gap-2">
                                  <span className="h-2.5 w-2.5 rounded-sm" style={{ background: s.sourceColor }} aria-hidden />
                                  <span className="font-medium text-ink-900">{s.sourceName}</span>
                                  {s.sourceKind === "PARTNER" && <Badge>Miles held</Badge>}
                                </span>
                                <span className="tnum text-ink-600">
                                  {formatPoints(s.availablePoints)} at {s.ratioLabel}
                                </span>
                                <span className="tnum font-medium text-ink-900">
                                  → {formatPoints(s.reachableUnits)} miles
                                </span>
                                {s.bonusUnits > 0 && (
                                  <Badge tone="good">includes {formatPoints(s.bonusUnits)} bonus</Badge>
                                )}
                                {!s.isActive && <Badge tone="critical">Not available</Badge>}
                                {s.notes.map((n) => (
                                  <span key={n} className="text-ink-500">· {n}</span>
                                ))}
                              </li>
                            ))}
                          </ul>
                        </td>
                      </tr>
                    )}
                  </Fragment>
                ))}
              </tbody>
            </table>
          </div>
          <p className="mt-3 text-xs text-ink-500">
            “Can add” is what each route would take today, after your caps and transfer blocks, counting each card
            or partner balance once via its best route. The totals overlap — the same points can only go to one
            programme.
          </p>
        </Panel>
      </div>
    </>
  );
}

/* ------------------------------------------------------------------ plan */

function PlanTab({ onError }: { onError: (m: string) => void }) {
  const [partners, setPartners] = useState<TransferPartner[] | null>(null);
  const [partnerId, setPartnerId] = useState("");
  const [target, setTarget] = useState("60000");
  const [useBalance, setUseBalance] = useState(true);
  const [onlyPersonId, setOnlyPersonId] = useState("");
  const [people, setPeople] = useState<Person[]>([]);
  const [result, setResult] = useState<SolveResult | null>(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api
      .get<{ partners: TransferPartner[] }>("/transfers/partners")
      .then((r) => {
        const active = r.partners.filter((p) => p.isActive);
        setPartners(active);
        setPartnerId((id) => id || active[0]?.id || "");
      })
      .catch((err) => onError(err instanceof ApiError ? err.message : "Could not load partners"));

    void api
      .get<{ people: Person[] }>("/people")
      .then((r) => setPeople(r.people))
      .catch(() => setPeople([]));
  }, [onError]);

  const solve = useCallback(
    async (e?: React.FormEvent) => {
      e?.preventDefault();
      const units = Number(target);
      if (!partnerId || !Number.isFinite(units) || units <= 0) return;
      setBusy(true);
      try {
        setResult(
          await api.post<SolveResult>("/transfers/solve", {
            partnerId,
            targetUnits: units,
            useExistingBalance: useBalance,
            ...(onlyPersonId ? { onlyPersonId } : {}),
          }),
        );
      } catch (err) {
        onError(err instanceof ApiError ? err.message : "Could not work that out");
      } finally {
        setBusy(false);
      }
    },
    [partnerId, target, useBalance, onlyPersonId, onError],
  );

  if (!partners) return <Spinner />;
  if (partners.length === 0) {
    return <EmptyState title="No active partners" hint="Add one under “Partners & routes” first." />;
  }

  return (
    <>
      <Panel className="mb-6">
        <form onSubmit={solve} className="space-y-4">
          <div className="grid gap-4 sm:grid-cols-3">
            <div className="sm:col-span-2">
              <label className="label" htmlFor="s-partner">I want miles in</label>
              <select id="s-partner" className="input" value={partnerId} onChange={(e) => setPartnerId(e.target.value)}>
                {partners.map((p) => (
                  <option key={p.id} value={p.id}>
                    {p.name} — {formatPoints(p.balance?.balanceUnits ?? 0)} held
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="label" htmlFor="s-target">Miles I need</label>
              <input
                id="s-target"
                className="input tnum"
                type="number"
                min={1}
                required
                value={target}
                onChange={(e) => setTarget(e.target.value)}
              />
            </div>
          </div>

          {people.length > 1 && (
            <div className="sm:max-w-xs">
              <label className="label" htmlFor="s-person">Draw points from</label>
              <select
                id="s-person"
                className="input"
                value={onlyPersonId}
                onChange={(e) => setOnlyPersonId(e.target.value)}
              >
                <option value="">Everyone&apos;s cards</option>
                {people.map((p) => (
                  <option key={p.id} value={p.id}>{p.name} only</option>
                ))}
              </select>
            </div>
          )}

          <div className="flex flex-wrap items-center gap-6">
            <label className="flex items-center gap-2 text-sm text-ink-700">
              <input
                type="checkbox"
                className="h-4 w-4 rounded border-ink-300 text-accent-600 focus:ring-accent-500"
                checked={useBalance}
                onChange={(e) => setUseBalance(e.target.checked)}
              />
              Count the miles I already hold there
            </label>
            <button className="btn-primary ml-auto" disabled={busy}>
              {busy ? "Working…" : "Find the cheapest route"}
            </button>
          </div>
        </form>
      </Panel>

      {result && <SolveOutput result={result} />}
    </>
  );
}

function SolveOutput({ result }: { result: SolveResult }) {
  if (result.alreadyThere) {
    return (
      <div className="rounded-xl border border-emerald-200 bg-emerald-50 p-5">
        <p className="text-sm font-medium text-emerald-900">
          You already hold {formatPoints(result.balanceUnits)} {result.partnerName} miles — that covers{" "}
          {formatPoints(result.targetUnits)}. No transfer needed.
        </p>
      </div>
    );
  }

  const { best } = result;

  return (
    <>
      {best ? (
        <div
          className={`mb-6 rounded-xl border p-5 ${
            best.complete ? "border-accent-200 bg-accent-50" : "border-amber-200 bg-amber-50"
          }`}
        >
          <p className={`text-xs font-medium uppercase tracking-wide ${best.complete ? "text-accent-800" : "text-amber-800"}`}>
            {best.complete ? "Cheapest way there" : "As close as you can get today"}
          </p>
          <h2 className="mt-2 text-xl font-semibold text-ink-950">
            {best.legs.length === 1 ? best.legs[0].sourceName : `${best.legs.length} sources combined`}
          </h2>
          <p className="mt-1 text-sm text-ink-700">
            {formatPoints(best.pointsUsed)} points → <strong>{formatPoints(best.unitsDelivered)}</strong> miles, giving up{" "}
            {formatINR(best.cost)} of value ({formatINR(best.costPerUnit, { decimals: true })} per mile).
            {best.bonusUnits > 0 && ` Includes ${formatPoints(best.bonusUnits)} bonus miles.`}
            {best.overshoot > 0 && ` Transfer blocks mean ${formatPoints(best.overshoot)} miles more than you asked for.`}
            {!best.complete && ` Still ${formatPoints(best.shortfall)} short of ${formatPoints(result.unitsNeeded)}.`}
          </p>

          <ol className="mt-4 space-y-2">
            {best.legs.map((leg, i) => (
              <li key={`${leg.sourceId}-${i}`} className="rounded-lg bg-white/70 px-3 py-2 text-sm">
                <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1">
                  <span className="tnum text-xs text-ink-400">{i + 1}</span>
                  <span className="inline-flex items-center gap-2">
                    <span className="h-2.5 w-2.5 rounded-sm" style={{ background: leg.sourceColor }} aria-hidden />
                    <span className="font-medium text-ink-900">{leg.sourceName}</span>
                  </span>
                  {leg.personName && (
                    <Badge tone={leg.crossesPeople ? "warning" : "neutral"}>
                      {leg.crossesPeople ? `${leg.personName}'s points` : leg.personName}
                    </Badge>
                  )}
                  <span className="tnum text-ink-700">{formatPoints(leg.points)} {leg.currencyName}</span>
                  <span className="tnum font-medium text-ink-900">→ {formatPoints(leg.units)} miles</span>
                  <span className="tnum ml-auto text-xs text-ink-500">{formatINR(leg.cost)}</span>
                </div>
                {/* Show the working when the points pass through another
                    programme, or pick up a bonus on the way. */}
                {(leg.hops.length > 1 || leg.bonusUnits > 0) && (
                  <ol className="mt-1.5 space-y-0.5 border-l-2 border-ink-200 pl-3">
                    {leg.hops.map((h, hi) => (
                      <li key={hi} className="tnum text-xs text-ink-600">
                        {h.fromLabel} → {h.toLabel}: {formatPoints(h.input)} at {h.ratioLabel} ={" "}
                        {formatPoints(h.baseOutput)}
                        {h.bonusOutput > 0 && (
                          <span className="font-medium text-emerald-700"> + {formatPoints(h.bonusOutput)} bonus</span>
                        )}
                      </li>
                    ))}
                    {leg.stranded > 0 && (
                      <li className="tnum text-xs text-amber-700">
                        {formatPoints(leg.stranded)} left behind — the next leg only moves whole blocks
                      </li>
                    )}
                  </ol>
                )}
              </li>
            ))}
          </ol>
        </div>
      ) : (
        <EmptyState
          title="Nothing can reach this programme"
          hint="Check the routes from your reward programmes, and whether a card is restricted to certain partners."
        />
      )}

      {result.plans.length > 0 && (
        <Panel title="Route by route" subtitle={`Each source on its own, against the ${formatPoints(result.unitsNeeded)} miles you still need`}>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-ink-200">
                  <th className="th">Source</th>
                  <th className="th text-right">Points</th>
                  <th className="th text-right">Miles</th>
                  <th className="th text-right">Value given up</th>
                  <th className="th text-right">Per mile</th>
                  <th className="th">Reach</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-ink-100">
                {result.plans.map((p, idx) => {
                  const leg = p.legs[0];
                  return (
                    <tr key={`${leg.sourceId}-${idx}`}>
                      <td className="td">
                        <span className="inline-flex items-center gap-2">
                          <span className="h-2.5 w-2.5 shrink-0 rounded-sm" style={{ background: leg.sourceColor }} aria-hidden />
                          <span>
                            <span className="font-medium text-ink-900">{leg.sourceName}</span>
                            <span className="block text-xs text-ink-500">
                              {leg.currencyName} at {leg.ratioLabel}
                              {leg.bonusUnits > 0 && ` · ${formatPoints(leg.bonusUnits)} bonus`}
                            </span>
                          </span>
                        </span>
                      </td>
                      <td className="td tnum text-right">{formatPoints(leg.points)}</td>
                      <td className="td tnum text-right">{formatPoints(p.unitsDelivered)}</td>
                      <td className="td tnum text-right">{formatINR(p.cost)}</td>
                      <td className="td tnum text-right">{formatINR(p.costPerUnit, { decimals: true })}</td>
                      <td className="td">
                        {p.complete ? (
                          <Badge tone="good">Covers it</Badge>
                        ) : (
                          <span className="text-xs text-amber-700">short by {formatPoints(p.shortfall)}</span>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {result.blocked.length > 0 && (
            <div className="mt-4 border-t border-ink-200 pt-3">
              <p className="mb-1.5 text-xs font-medium text-ink-700">Cards that can't help here</p>
              <ul className="space-y-1">
                {result.blocked.map((b, i) => (
                  <li key={`${b.sourceName}-${i}`} className="text-xs text-ink-500">
                    <span className="font-medium text-ink-700">{b.sourceName}</span> — {b.reason}
                  </li>
                ))}
              </ul>
            </div>
          )}
        </Panel>
      )}
    </>
  );
}
